import React, { useState } from 'react';
import { checkKeyFormat, activateLicense, PlanType } from '../lib/license';

interface LicenseScreenProps {
  onStartDemo: () => void;
  onActivate: (key: string, plan: PlanType, expiresAt?: string | null, customer?: string) => void;
  expirationMessage?: string;
}

export const LicenseScreen = ({ onStartDemo, onActivate, expirationMessage }: LicenseScreenProps) => {
  const [licenseKey, setLicenseKey] = useState('');
  const [showKeyInput, setShowKeyInput] = useState(!!expirationMessage); // auto-show if expired/revoked
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isExpiredOrRevoked = !!expirationMessage;

  const handleActivate = async () => {
    setError('');
    setSuccess('');
    const key = licenseKey.trim().toUpperCase();

    if (!key) { setError('Please enter a license key.'); return; }
    if (key.length < 10) { setError('Invalid license key format.'); return; }

    setLoading(true);
    try {
      const result = await checkKeyFormat(key);
      if (!result.valid) {
        setError(result.reason || 'Invalid or expired license key.');
        return;
      }

      // Activate — sync to Firestore. MUST check the result: if the server-side
      // write fails (network, permission, device-limit reached, etc.) the app
      // must NOT show success — otherwise it silently unlocks locally while the
      // admin panel still shows the license as pending/unused.
      const activation = await activateLicense(key);
      if (!activation.success) {
        setError(activation.reason || 'Activation failed. Please try again.');
        return;
      }

      setSuccess(`✓ Activated successfully! Welcome${result.customer ? ', ' + result.customer : ''}!`);
      setTimeout(() => {
        onActivate(key, result.plan, result.expiresAt, result.customer);
      }, 800);
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleActivate();
  };

  // Auto-format key as user types: XXXX-XXXX-XXXX-XXXX
  const handleKeyInput = (val: string) => {
    const clean = val.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 16);
    const formatted = clean.match(/.{1,4}/g)?.join('-') || clean;
    setLicenseKey(formatted);
    setError('');
  };

  return (
    <div className="flex flex-col items-center justify-center h-screen w-full bg-[#f1f5f9] font-sans">
      <div className="bg-white rounded shadow-xl border border-gray-300 w-full max-w-md overflow-hidden">

        {/* Header */}
        <div className="bg-[#1e8585] p-5 text-center shadow-sm">
          <h1 className="text-2xl font-bold text-white italic tracking-wider">Aarthika Pro</h1>
          <p className="text-[#cffafe] text-xs mt-1 tracking-wide">Professional Accounting Software</p>
        </div>

        <div className="p-8 flex flex-col items-center">
          <h2 className="text-lg font-bold text-gray-800 mb-3">
            {isExpiredOrRevoked ? 'License Required' : 'Welcome!'}
          </h2>

          {/* Expiration / Revocation Message */}
          {expirationMessage && (
            <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded mb-4 text-sm w-full text-center">
              {expirationMessage}
            </div>
          )}

          {!expirationMessage && (
            <p className="text-[13px] text-gray-500 mb-6 text-center leading-relaxed">
              Please activate your license or start a free 7-day trial to continue.
            </p>
          )}

          {/* Key Input */}
          {showKeyInput ? (
            <div className="flex flex-col space-y-3 w-full">
              <label className="text-xs font-semibold text-gray-600 uppercase tracking-wide">License Key</label>
              <input
                type="text"
                placeholder="XXXX-XXXX-XXXX-XXXX"
                className={`w-full px-3 py-2.5 border rounded outline-none focus:border-[#1e8585] text-[14px] font-mono tracking-widest text-center uppercase ${
                  error ? 'border-red-400 bg-red-50' : success ? 'border-green-400 bg-green-50' : 'border-gray-300'
                }`}
                value={licenseKey}
                onChange={(e) => handleKeyInput(e.target.value)}
                onKeyDown={handleKeyDown}
                autoFocus
                disabled={loading}
                maxLength={19}
                spellCheck={false}
              />

              {error && (
                <p className="text-red-500 text-xs flex items-center gap-1">
                  <span>⚠</span> {error}
                </p>
              )}
              {success && (
                <p className="text-green-600 text-xs font-semibold flex items-center gap-1">
                  <span>✓</span> {success}
                </p>
              )}

              <button
                onClick={handleActivate}
                disabled={loading || !!success}
                className="w-full py-2.5 bg-[#1e8585] hover:bg-[#166f6f] text-white font-bold rounded text-[13px] disabled:opacity-60 transition-colors mt-1"
              >
                {loading ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                    </svg>
                    Validating...
                  </span>
                ) : 'Activate License'}
              </button>

              {!isExpiredOrRevoked && (
                <button
                  onClick={() => { setShowKeyInput(false); setError(''); setLicenseKey(''); setSuccess(''); }}
                  disabled={loading}
                  className="w-full py-2 bg-gray-100 text-gray-600 hover:bg-gray-200 font-semibold rounded text-[13px] transition-colors"
                >
                  ← Back
                </button>
              )}
            </div>
          ) : (
            <div className="w-full flex flex-col space-y-3">
              <button
                onClick={() => setShowKeyInput(true)}
                className="w-full py-2.5 bg-[#1e8585] hover:bg-[#166f6f] text-white font-bold rounded shadow-sm transition-colors text-[13px] tracking-wide"
              >
                🔑 Activate License
              </button>
              <button
                onClick={onStartDemo}
                className="w-full py-2.5 bg-white hover:bg-gray-50 text-[#1e8585] font-bold border border-[#1e8585] rounded shadow-sm transition-colors text-[13px] tracking-wide"
              >
                ▶ Start 7-Day Free Trial
              </button>
              <p className="text-center text-[11px] text-gray-400 pt-1">
                No credit card required for trial
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 border-t border-gray-200 px-6 py-3 text-center">
          <p className="text-[11px] text-gray-400">
            Need a license? Contact <span className="text-[#1e8585] font-semibold">shreejiinfosys.com</span>
          </p>
        </div>
      </div>
    </div>
  );
};
